// deployment_class: customer_env_allowed
/**
 * Landing — the PUBLIC marketing landing for Kathedral.ai (Version B · route /landing).
 * Animated previews of the app interior (executive hub, workflows, the deal
 * marketplace, Ask Kathedral), all in the current transparent-glass system over the
 * photographic backdrop, ending at the site footer. All names + figures here are
 * ILLUSTRATIVE and fictional — deliberately distinct from any stored data, since this
 * is a public surface (E3 2026-08-01). Reveal/stepper/ring/typing animations are CSS; a small
 * IntersectionObserver adds `.kh-in` as sections scroll into view; all motion respects
 * prefers-reduced-motion. E3 2026-08-01.
 *
 * Interaction model (E3 2026-08-01, refined): clicking ANY headline surface — the
 * hero hub summary + each KPI tile, the stream cards, the workflow card, the deal
 * cards, or the hero Play ▶ — launches a self-running AUTO-PLAY CINEMATIC
 * (LandingConstellation) that dives the data lineage, magnifies the crux, hands
 * back Kathedral's AI advisory, and lands on the account gate. The WHOLE card is
 * the click target (no explore icons, no "click here" hints) — the clean cards +
 * the cinematic do the work. North star: "we DO NOT want to lose anyone."
 */
import { useEffect, useRef, useState } from 'react';
import { Footer } from './components/Footer';
import { WORKFLOWS } from './components/landingDrillData';
import { LandingConstellation } from './components/LandingConstellation';
import './components/landing.css';
import './components/landing-drill.css';
import { RichText } from '../shared/ui/RichText';

/** Phone breakpoint: below this the full cinematic is replaced by the compact
 * tooltip hint (too little room for the immersive reel on a phone · E3 2026-08-01).
 * Kept in step with the landing's own phone media queries. */
const MOBILE_QUERY = '(max-width: 640px)';

/** The active drill-in — always the FULL data-lineage constellation, opened focused
 * on the clicked surface's node (E3: "do this for EVERYTHING"). */
interface Drill {
  focusId: string;
  label: string;
  sub: string;
  origin?: { xPct: number; yPct: number };
}
/** Workflow / deal slug → its constellation node (the focus target). */
const DEAL_NODE: Record<string, string> = {
  'harbor-point': 'pr-harbor',
  'summit-crossing': 'pr-summit',
  'meadow-glen': 'pr-meadow',
  'park-terrace': 'pr-park',
};

type CSSVars = React.CSSProperties & Record<string, string | number>;

/** Ask Kathedral — the reel of real questions a PHA CEO would actually ask. Each
 * cycles: question → (optional) AI "thinking"/forecasting beat → typed answer →
 * hold. `think` marks the forecasting cases where the AI reasons before answering. */
const ASK_QA: ReadonlyArray<{ q: string; a: string; src: string; think?: string }> = [
  {
    q: "If the CFO doesn't confirm Harbor Point by Friday, what slips?",
    a: 'The Aug 15 close slips to September — and the 9% LIHTC allocation is at risk past the placed-in-service deadline.',
    src: 'the Harbor Point signing workflow',
  },
  {
    q: 'Can we close the Meadow Glen $1.5M gap without new subsidy?',
    a: 'Yes — a deferred developer fee covers it. Two comparable deals closed the same way.',
    src: 'the Meadow Glen pro-forma',
    think: 'Modeling the deferred-fee cash flows against Meadow Glen’s 15-year pro-forma…',
  },
  {
    q: 'What happens if Summit Crossing stays under the DSCR floor?',
    a: 'At 1.18 the bond can’t close as sized. Resizing down $2.1M brings it to 1.21.',
    src: 'the Summit Crossing snapshot',
    think: 'Running debt-service scenarios across three bond sizes…',
  },
  {
    q: 'Are we going to hit the 2030 housing goal?',
    a: 'At the current pace, 1,780 of 1,820 units by 2030 — 40 short. Park Terrace’s second phase closes it.',
    src: 'the strategic plan',
    think: 'Forecasting delivery across the four active partnerships…',
  },
  {
    q: 'Which deal is most likely to slip this quarter?',
    a: 'Harbor Point — the only one with a hard external deadline and an open internal gate.',
    src: 'the projects stream',
  },
  {
    q: 'What’s our cash position through year-end?',
    a: 'Covered — the tight month is October, when the Harbor Point and Meadow Glen draws overlap.',
    src: 'the distribution ledger',
    think: 'Projecting monthly cash against the two draw schedules…',
  },
  {
    q: 'Should I approve all three resolutions Thursday?',
    a: 'Harbor Point and Procurement are clean. Hold Summit Crossing for the resize, or approve with the coverage note.',
    src: 'the July resolutions',
  },
  {
    q: 'If REAC flags us in August, what’s the remediation timeline?',
    a: 'Typically 60–90 days. I can pre-stage the response so we’re ready the day the score lands.',
    src: 'the oversight stream',
  },
  {
    q: 'How much developer fee is deferred across the portfolio?',
    a: '$4.2M across Harbor Point, Meadow Glen, and the proposed Summit Crossing resize — all within tolerance.',
    src: 'the partnership snapshots',
  },
  {
    q: 'Draft my Summit Crossing talking points for the board.',
    a: 'DSCR 1.18 vs the 1.20 floor; a $2.1M resize clears it. Expect a question on the coverage cushion.',
    src: 'resolution R-2026-15',
  },
];

/** The Hub preview runs as a time-lapse — each month advances the executive
 * figures so the dashboard is visibly *living*: pipeline and units grow, the
 * goal % climbs, the next board + compliance shift. Ends on the present (Jul '26)
 * then loops. Numbers tween between months; the month chip fades on each change. */
const MONTHS: ReadonlyArray<{
  mo: string; pct: number; pipe: number; units: number; part: number;
  decisions: number; board: string; packet: string; comp: number; compNote: string;
}> = [
  { mo: 'Apr', pct: 68, pipe: 402, units: 256, part: 8, decisions: 1, board: 'Apr 9', packet: 'closed', comp: 0, compNote: 'all clear' },
  { mo: 'May', pct: 71, pipe: 431, units: 274, part: 9, decisions: 2, board: 'May 14', packet: 'closed', comp: 1, compNote: 'MTW cert · cleared' },
  { mo: 'Jun', pct: 74, pipe: 458, units: 296, part: 9, decisions: 2, board: 'Jun 11', packet: 'closed', comp: 0, compNote: 'all clear' },
  { mo: 'Jul', pct: 78, pipe: 478, units: 312, part: 10, decisions: 3, board: 'Jul 14', packet: '60% assembled', comp: 1, compNote: 'REAC Aug 30 · else clear' },
];
const RING_CIRC = 204; // 2π·32.5 ≈ dasharray of the progress ring

// WORKFLOWS now lives in ./components/landingDrillData (single source — the app
// cockpit reads the same steps, so the landing stepper + the demo never drift).

export function LandingPreview(): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const [askIdx, setAskIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<'think' | 'type'>('type');
  const [wfIdx, setWfIdx] = useState(0);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [streamsHot, setStreamsHot] = useState(false);
  const [monthIdx, setMonthIdx] = useState(0);
  const [disp, setDisp] = useState({ pct: MONTHS[0]!.pct, pipe: MONTHS[0]!.pipe, units: MONTHS[0]!.units });
  const dispRef = useRef(disp);
  dispRef.current = disp;

  // ── The auto-play cinematic drill: launched from any headline surface ──
  const [drill, setDrill] = useState<Drill | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // ── Mobile: the immersive cinematic is too much for a phone (E3 2026-08-01), and
  //    the tap-hint popover was REMOVED for mobile only (E3 2026-08-04). Below
  //    MOBILE_QUERY, tapping a card is a no-op — the cards are display-only and the
  //    persistent Sign in / Apply CTA carries conversion. Desktop keeps the cinematic.

  /** Center-of-element in viewport %, so the cinematic card grows from the click. */
  const originOfEl = (el: HTMLElement): { xPct: number; yPct: number } => {
    const r = el.getBoundingClientRect();
    return {
      xPct: ((r.left + r.width / 2) / window.innerWidth) * 100,
      yPct: ((r.top + r.height / 2) / window.innerHeight) * 100,
    };
  };
  const openFrom = (el: HTMLElement, focusId: string, label: string, sub: string): void => {
    triggerRef.current = el;
    // Phones: no cinematic AND no hint popover — display-only (E3 2026-08-04).
    if (window.matchMedia?.(MOBILE_QUERY).matches) return;
    setDrill({ focusId, label, sub, origin: originOfEl(el) });
  };
  /** For native <button> surfaces (hero Play, hub summary, KPI tiles). */
  const openWeb =
    (focusId: string, label: string, sub: string) =>
    (e: React.MouseEvent<HTMLElement>): void =>
      openFrom(e.currentTarget, focusId, label, sub);
  /** For whole-card surfaces made keyboard-operable via role="button" (streams,
   * the workflow card, deal cards). Only the card itself activates on Enter/Space
   * — the `e.target !== e.currentTarget` guard ignores keys bubbling from any
   * descendant. (The workflow pager is a SIBLING of the card, never a child, so it
   * is not nested-interactive and its click never bubbles into the card.) */
  const play = (focusId: string, label: string, sub: string) => ({
    onClick: (e: React.MouseEvent<HTMLElement>) => openFrom(e.currentTarget, focusId, label, sub),
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openFrom(e.currentTarget, focusId, label, sub);
      }
    },
  });
  const closeDrill = (): void => {
    setDrill(null);
    // Return focus to whatever opened the drill (WCAG 2.2 focus-return).
    window.setTimeout(() => triggerRef.current?.focus?.(), 0);
  };

  // The Hub preview is a time-lapse: advance the month on a cadence (parks on the
  // present month if the viewer prefers reduced motion).
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce) {
      setMonthIdx(MONTHS.length - 1);
      return;
    }
    const id = window.setInterval(() => setMonthIdx((n) => (n + 1) % MONTHS.length), 2900);
    return () => window.clearInterval(id);
  }, []);

  // Tween the preview figures from the current shown values to the new month's.
  useEffect(() => {
    const target = MONTHS[monthIdx];
    if (!target) return;
    const from = dispRef.current;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce) {
      setDisp({ pct: target.pct, pipe: target.pipe, units: target.units });
      return;
    }
    const dur = 950;
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number): void => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setDisp({
        pct: Math.round(from.pct + (target.pct - from.pct) * e),
        pipe: Math.round(from.pipe + (target.pipe - from.pipe) * e),
        units: Math.round(from.units + (target.units - from.units) * e),
      });
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [monthIdx]);

  // Ask Kathedral: type the current answer, hold, then advance to the next Q&A.
  useEffect(() => {
    const qa = ASK_QA[askIdx];
    if (!qa) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setTyped('');
    if (reduce) {
      setPhase('type');
      setTyped(qa.a);
      return;
    }
    const timers: number[] = [];
    const startTyping = (): void => {
      setPhase('type');
      let i = 0;
      const type = window.setInterval(() => {
        i += 1;
        setTyped(qa.a.slice(0, i));
        if (i >= qa.a.length) {
          window.clearInterval(type);
          timers.push(window.setTimeout(() => setAskIdx((n) => (n + 1) % ASK_QA.length), 4200));
        }
      }, 52);
      timers.push(type);
    };
    if (qa.think) {
      setPhase('think');
      timers.push(window.setTimeout(startTyping, 2000));
    } else {
      startTyping();
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [askIdx]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reveal = Array.from(root.querySelectorAll<HTMLElement>('.kh-reveal'));
    const counts = Array.from(root.querySelectorAll<HTMLElement>('.kh-count'));
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    const countUp = (el: HTMLElement): void => {
      const target = Number(el.dataset['count'] ?? '0');
      const prefix = el.dataset['prefix'] ?? '';
      const suffix = el.dataset['suffix'] ?? '';
      if (reduce) {
        el.textContent = `${prefix}${target.toLocaleString()}${suffix}`;
        return;
      }
      const dur = 1300;
      const start = performance.now();
      const tick = (now: number): void => {
        const t = Math.min(1, (now - start) / dur);
        const v = Math.round((1 - Math.pow(1 - t, 3)) * target);
        el.textContent = `${prefix}${v.toLocaleString()}${suffix}`;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      reveal.forEach((el) => el.classList.add('kh-in'));
      counts.forEach(countUp);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('kh-in');
          if ((e.target as HTMLElement).classList.contains('kh-count')) countUp(e.target as HTMLElement);
          io.unobserve(e.target);
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );
    reveal.forEach((el) => io.observe(el));
    counts.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const qa = ASK_QA[askIdx] as (typeof ASK_QA)[number];
  const answerDone = typed === qa.a;
  const wf = WORKFLOWS[wfIdx] as (typeof WORKFLOWS)[number];
  const mo = MONTHS[monthIdx] as (typeof MONTHS)[number];

  return (
    <div className="kh-landing" ref={rootRef}>
      {/* ── HERO ── */}
      <div className="kh-wrap">
        <section className="kh-hero">
          <div className="kh-hero-copy kh-reveal">
            <span className="kh-hero-eyebrow">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2l1.9 5.6L19.5 9l-4.6 3.3L16 18l-4-3.4L8 18l1.1-5.7L4.5 9l5.6-1.4L12 2z" fill="currentColor" />
              </svg>
              Executive intelligence platform
            </span>
            <h1>
              <span className="kh-flip">
                <span className="kh-flip-word">Structured partnerships.</span>
                <span className="kh-flip-word">Provable outcomes.</span>
                <span className="kh-flip-word">One executive anchor.</span>
              </span>
            </h1>
            <div className="kh-cta-row">
              {/* Front door to the cinematic — the play badge (E3 1e); clicking plays
                  the marquee reel: the whole agency → Summit crux → AI advisory → gate. */}
              <button
                type="button"
                className="kh-cta kh-cta--play"
                onClick={openWeb('core', 'The whole agency', 'Lakeside Housing Authority · every data point traced to source')}
                aria-label="Play the live demo"
              >
                Explore Kathedral
                <span className="kh-cta-play" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
                </span>
              </button>
            </div>
          </div>

          {/* live dashboard preview — a time-lapse: the month advances and every
              figure moves with it (see MONTHS + the tween effect above) */}
          <div className="kh-glass kh-preview kh-reveal" style={{ '--i': 1 } as CSSVars}>
            <div className="kh-preview-head">
              <span className="kh-preview-title">Executive · The Hub</span>
              <span className="kh-preview-asof" key={mo.mo}>{mo.mo} 2026</span>
              <span className="kh-preview-tab">Lakeside Housing Authority</span>
            </div>
            <button
              type="button"
              className="kh-apex kh-apex-btn kh-playcard"
              onClick={openWeb('core', 'Executive · The Hub', `${disp.pct}% to the 2030 plan · Lakeside Housing Authority`)}
              aria-label={`Play the executive hub data story — ${disp.pct}% to the 2030 plan`}
            >
              <svg className="kh-ring" viewBox="0 0 78 78" aria-hidden="true">
                <circle className="track" cx="39" cy="39" r="32.5" />
                <circle
                  className="prog"
                  cx="39"
                  cy="39"
                  r="32.5"
                  style={{ strokeDashoffset: RING_CIRC * (1 - disp.pct / 100) }}
                />
                <text x="39" y="46" textAnchor="middle">{disp.pct}%</text>
              </svg>
              <p>
                Lakeside is <span className="pos">on track</span>. You're <b>{disp.pct}%</b> toward
                the five-year housing plan — <span className="neg">{mo.decisions} decision{mo.decisions === 1 ? '' : 's'}</span>{' '}
                waiting and two deals to watch.
              </p>
            </button>
            <div className="kh-kpis">
              <button type="button" className="kh-kpi kh-kpi-btn kh-playcard" onClick={openWeb('pr-pipe', 'Pipeline fee income', `$${disp.pipe}M · ${mo.part} active partnerships`)} aria-label={`Play the pipeline fee income data story, $${disp.pipe}M`}>
                <div className="k">Pipeline fee income</div><div className="v">${disp.pipe}M</div><div className="s">{mo.part} active partnerships</div>
              </button>
              <button type="button" className="kh-kpi kh-kpi-btn kh-playcard" onClick={openWeb('pr-units', "Units delivered ’26", `${disp.units} · on pace vs 280`)} aria-label={`Play the units delivered data story, ${disp.units}`}>
                <div className="k">Units delivered ’26</div><div className="v">{disp.units}</div><div className="s">▲ on pace vs 280</div>
              </button>
              <button type="button" className="kh-kpi kh-kpi-btn kh-playcard" onClick={openWeb('board', 'Next board', `${mo.board} · packet ${mo.packet}`)} aria-label={`Play the next board data story, ${mo.board}`}>
                <div className="k">Next board</div><div className="v" key={`b${mo.mo}`}>{mo.board}</div><div className="s">packet {mo.packet}</div>
              </button>
              <button type="button" className="kh-kpi kh-kpi-btn kh-playcard" onClick={openWeb('ov-comp', 'Compliance', `${mo.comp} flag · ${mo.compNote}`)} aria-label="Play the compliance data story">
                <div className="k">Compliance</div><div className="v" key={`c${mo.mo}`}>{mo.comp}</div><div className="s">{mo.compNote}</div>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* ── CONTENT (frosted veil over the photo) ── */}
      <div className="kh-content">
        {/* Section 1 — the hub / streams */}
        <section className="kh-section">
          <div className="kh-wrap">
            <div className="kh-section-head kh-reveal">
              <div className="kh-eyebrow">The Hub</div>
              <h2>See the whole agency at a glance.</h2>
            </div>
            <div
              className="kh-streams"
              onMouseEnter={() => setStreamsHot(true)}
              onMouseLeave={() => setStreamsHot(false)}
              onFocus={() => setStreamsHot(true)}
              onBlur={() => setStreamsHot(false)}
            >
              {[
                { i: '◷', n: 'Planning', hub: 'planning', sub: 'Strategic plan · 8 of 9 objectives on track', rag: ['y', 'Watch'], bl: 'Objectives on track', w: 89, c: 'g', v: '8/9', m: 'Resident-services staffing a quarter behind.',
                  bt: 'Strategic priorities', back: ['Vibrant Supply <b>71%</b>', 'High-Perf Gov <b>54%</b>', 'Resident Wellbeing <span class="bad">42%</span>', 'Next review <b>Jul 22</b>'] },
                { i: '⌂', n: 'Projects', hub: 'projects', sub: '$478M pipeline · 2 deals watched', rag: ['r', '2 at risk'], bl: 'Healthy deals', w: 80, c: 'g', v: '8/10', m: '<b>$478M</b> pipeline · Summit Crossing + Meadow Glen watched.',
                  bt: 'Watched deals', back: ['Summit Crossing <span class="bad">DSCR 1.18</span>', 'Meadow Glen <span class="bad">$1.5M gap</span>', 'Harbor Point <b>on track · Aug 15</b>', '8 others <b>healthy</b>'] },
                { i: '⚖', n: 'Board', hub: 'board', sub: '3 resolutions awaiting sign-off', rag: ['y', '3 awaiting'], bl: 'Packet assembled', w: 60, c: 'y', v: '60%', m: 'Jul 14 · <b>3 resolutions</b> need sign-off.',
                  bt: 'Resolutions', back: ['R-2026-14 <b>Harbor Point JV</b>', 'R-2026-15 <span class="bad">Summit Crossing</span>', 'R-2026-16 <b>Procurement</b>', 'Quorum <b>confirmed</b>'] },
                { i: '◉', n: 'Oversight', hub: 'oversight', sub: '11 of 14 departments · 1 compliance flag', rag: ['g', 'On track'], bl: 'Depts on track', w: 79, c: 'g', v: '11/14', m: 'One compliance flag: REAC Aug 30.',
                  bt: 'Departments', back: ['On track <b>11/14</b>', 'Initiatives <b>64%</b>', 'Compliance <span class="bad">1 flag</span>', 'REAC window <b>Aug 30</b>'] },
              ].map((s, idx) => (
                <div key={s.n} className="kh-stream kh-reveal" style={{ '--i': idx } as CSSVars}>
                  <div
                    className="kh-glass kh-stream-card kh-playcard"
                    role="button"
                    tabIndex={0}
                    aria-label={`Play the ${s.n} data story`}
                    {...play(s.hub, s.n, s.sub)}
                  >
                    <div className="kh-stream-top">
                      <span className="kh-stream-ico">{s.i}</span>
                      <span className="kh-stream-name">{s.n}</span>
                      <span className={`kh-rag ${s.rag[0]}`}>{s.rag[1]}</span>
                    </div>
                    <div className="kh-bar">
                      <div className="kh-bar-lbl"><span>{s.bl}</span><span>{s.v}</span></div>
                      <div className="kh-bar-track"><div className={`kh-bar-fill ${s.c}`} style={{ '--w': `${s.w}%` } as CSSVars} /></div>
                    </div>
                    <div className="kh-stream-meta"><RichText value={s.m} /></div>
                    {/* drill-down — expands in place (smooth drawer) as informative detail */}
                    <div className="kh-stream-detail">
                      <div className="kh-stream-detail-inner">
                        <div className="kh-back-title">{s.bt}</div>
                        {s.back.map((r) => (
                          <div key={r} className="kh-back-row"><RichText value={r} /></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2 — workflows (dims while a Hub drill-down overlay is open) */}
        <section className={`kh-section${streamsHot ? ' kh-dim' : ''}`}>
          <div className="kh-wrap">
            <div className="kh-section-head kh-reveal">
              <div className="kh-eyebrow">Workflows</div>
              <h2>Work with AI Advisory.</h2>
            </div>
            {/* The pager is a SIBLING of the card (not a child) — a <button> nested
                inside the role="button" card would be a WCAG nested-interactive
                violation. It sits absolutely over the card's top-right corner. */}
            <div className="kh-flow-wrap kh-reveal">
              <div
                className="kh-glass kh-flow kh-playcard"
                role="button"
                tabIndex={0}
                aria-label={`Play the ${wf.title} data story`}
                {...play(DEAL_NODE[wf.id] ?? 'core', wf.title, wf.sub)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                <div className="kh-flow-head">
                  <div>
                    <div className="kh-flow-title">{wf.title}</div>
                    <div className="kh-flow-sub">{wf.sub}</div>
                  </div>
                </div>
                <div className="kh-steps" key={wfIdx}>
                  {wf.steps.map((step, idx) => (
                    <div
                      key={step.l}
                      className={`kh-step ${step.st} ${hoveredStep === idx ? 'hot' : ''}`}
                      style={{ '--s': idx } as CSSVars}
                      onMouseEnter={() => setHoveredStep(idx)}
                    >
                      <div className="kh-step-dot">{step.n}</div>
                      <div className="kh-step-lbl">{step.l}</div>
                    </div>
                  ))}
                </div>
                <div className={`kh-flow-detail ${hoveredStep !== null ? 'open' : ''}`}>
                  {hoveredStep !== null && wf.steps[hoveredStep] && (
                    <div className="kh-flow-detail-inner" key={`${wfIdx}-${hoveredStep}`}>
                      <span className="kh-flow-detail-step">{wf.steps[hoveredStep].l}</span>
                      <span className="kh-flow-detail-text">{wf.steps[hoveredStep].detail}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Cycles workflows independently of the card — a sibling, so its click
                  no longer bubbles into the card (no stopPropagation needed). */}
              <button
                type="button"
                className="kh-flow-next"
                onClick={() => {
                  setHoveredStep(null);
                  setWfIdx((n) => (n + 1) % WORKFLOWS.length);
                }}
                aria-label="Next project workflow"
              >
                ›
              </button>
            </div>
          </div>
        </section>

        {/* Section 3 — projects */}
        <section className="kh-section">
          <div className="kh-wrap">
            <div className="kh-section-head kh-reveal">
              <div className="kh-eyebrow">Partnerships</div>
              <h2>Every partnership, watched.</h2>
            </div>
            <div className="kh-projects">
              {[
                { n: 'Harbor Point', t: 'LIHTC 9%', node: 'pr-harbor', rag: ['y', 'ED review'], dev: 'Meridian Partners', tdc: '$62M', units: '84', note: 'phase 7 of 9',
                  back: ['Developer <b>Meridian Partners</b>', 'Structure <b>LIHTC 9% · P7</b>', 'Equity <b>$41M</b> · Debt <b>$21M</b>', 'Next <b>CFO confirm → Aug 15 close</b>', '⛓ Merkle chain <b>verified</b>'] },
                { n: 'Summit Crossing', t: 'Bond', node: 'pr-summit', rag: ['r', 'binding gate'], dev: 'Meridian Partners', tdc: '$54M', units: '72', note: 'DSCR 1.18',
                  back: ['Developer <b>Meridian Partners</b>', 'Structure <b>Tax-exempt Bond · P5</b>', 'DSCR <span class="bad">1.18</span> · floor <b>1.20</b>', 'Fix <b>resize −$2.1M → 1.21</b>', 'Gate <span class="bad">binding · breached</span>'] },
                { n: 'Meadow Glen', t: 'LIHTC 4%', node: 'pr-meadow', rag: ['y', 'pro-forma'], dev: 'Halcyon Development', tdc: '$33M', units: '110', note: '$1.5M gap',
                  back: ['Developer <b>Halcyon Development</b>', 'Structure <b>LIHTC 4% · P2</b>', 'Gap <span class="bad">$1.5M</span> · deferred fee', 'Review <b>Jul 22 working session</b>', '110 units · TDC <b>$33M</b>'] },
                { n: 'Park Terrace', t: 'Acquisition', node: 'pr-park', rag: ['g', 'active'], dev: 'Kestrel Group', tdc: '$41M', units: '60', note: 'stabilizing',
                  back: ['Developer <b>Kestrel Group</b>', 'Structure <b>Acquisition · P3</b>', 'Status <b>stabilizing · lease-up</b>', 'Coverage <b>1.34 DSCR</b>', '60 units · TDC <b>$41M</b>'] },
              ].map((p, idx) => (
                <div
                  key={p.n}
                  className="kh-proj kh-reveal kh-playcard"
                  style={{ '--i': idx } as CSSVars}
                  role="button"
                  tabIndex={0}
                  aria-label={`Play the ${p.n} data story`}
                  {...play(p.node, p.n, `${p.dev} · ${p.t}`)}
                >
                  <div className="kh-proj-inner">
                    <div className="kh-glass kh-proj-face kh-proj-front">
                      <div className="kh-proj-top">
                        <span className="kh-proj-name">{p.n} — {p.t}</span>
                        <span className={`kh-rag ${p.rag[0]}`}>{p.rag[1]}</span>
                      </div>
                      <div className="kh-proj-meta">{p.dev} · {p.note}</div>
                      <div className="kh-proj-stats">
                        <div className="kh-proj-stat"><div className="v">{p.tdc}</div><div className="k">Total dev cost</div></div>
                        <div className="kh-proj-stat"><div className="v">{p.units}</div><div className="k">Units</div></div>
                      </div>
                    </div>
                    <div className="kh-glass kh-proj-face kh-proj-back">
                      <div className="kh-back-title">Deal detail</div>
                      {p.back.map((r) => (
                        <div key={r} className="kh-back-row"><RichText value={r} /></div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4 — Ask Kathedral */}
        <section className="kh-section">
          <div className="kh-wrap">
            <div className="kh-section-head kh-reveal">
              <div className="kh-eyebrow">Ask Kathedral</div>
              <h2>Ask anything. Get the answer, sourced.</h2>
            </div>
            <div className="kh-glass kh-ask kh-reveal">
              <div className="kh-ask-q" key={`q${askIdx}`}>
                <span className="kh-q-ico" aria-hidden="true">›</span> {qa.q}
              </div>
              <div className="kh-ask-a">
                <span className="kh-ask-ico" aria-hidden="true">K</span>
                <div className="kh-ask-bubble">
                  {phase === 'think' && qa.think ? (
                    <span className="kh-thinking">
                      {qa.think}
                      <span className="kh-think-dots" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                      </span>
                    </span>
                  ) : (
                    <>
                      <span className="kh-typed">{typed}</span>
                      {!answerDone && <span className="kh-caret" aria-hidden="true" />}
                      {answerDone && (
                        <div className="kh-ask-src" key={`s${askIdx}`}>⛓ traces to {qa.src} · verify me</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* ── The auto-play cinematic: one data-lineage reel, wired to every headline
             surface — dive → magnify the crux → AI advisory takeover → account gate. ── */}
      {drill && (
        <LandingConstellation
          originLabel={drill.label}
          originSub={drill.sub}
          focusId={drill.focusId}
          originPoint={drill.origin}
          onClose={closeDrill}
        />
      )}
    </div>
  );
}
